<template>
  <div class="about-page">
    <!-- 导航栏 -->
    <WebsiteNavbar />

    <!-- 灰色横幅区域 -->
    <div class="hero-section">
      <div class="hero-background">
        <div class="hero-overlay"></div>
      </div>
      <div class="container">
        <h1 class="page-title">{{ safeT('about.hero.title', 'About Us') }}</h1>
        <div class="breadcrumb">
          <span>{{ safeT('about.breadcrumb.industrialParts', 'Gyre Industrial Mechanical parts') }}</span>
          <span class="separator">></span>
          <span>{{ safeT('about.breadcrumb.actuators', 'Pneumatic Actuators') }}</span>
          <span class="separator">></span>
          <span>{{ safeT('about.breadcrumb.controllers', 'Pneumatic Controllers') }}</span>
          <span class="separator">></span>
          <span>{{ safeT('about.breadcrumb.solutions', 'Customized Solutions') }}</span>
          <span class="separator">></span>
          <span class="current">{{ safeT('about.breadcrumb.current', 'About Us') }}</span>
        </div>
      </div>
    </div>

    <!-- 公司介绍区域 -->
    <section class="intro-section">
      <div class="container">
        <p class="intro-text">{{ safeT('about.intro.text', 'We are a system integrator specialized in pneumatic, fluidic, electro-drive and automation design, serving high-end sectors such as non-standard automation, semiconductors and medical devices with tailored engineering solutions.') }}</p>
      </div>
    </section>

    <!-- Welcome 和联系方式区域 -->
    <section class="welcome-section">
      <div class="container">
        <div class="logo-container">
          <img :src="logoImage" alt="Logo" class="company-logo" />
        </div>
        <h2 class="welcome-title">{{ safeT('about.welcome.title', 'Welcome') }}</h2>

        <div class="contact-info">
          <h3 class="contact-heading">{{ safeT('about.welcome.contact', 'How to get in touch?') }}</h3>

          <div class="contact-item">
            <svg class="icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
              <polyline points="22,6 12,13 2,6"></polyline>
            </svg>
            <a :href="`mailto:${safeT('about.welcome.email', 'hemu@gyansol.com')}`" class="contact-link">
              {{ safeT('about.welcome.email', 'hemu@gyansol.com') }}
            </a>
          </div>

          <div class="contact-item">
            <svg class="icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
            </svg>
            <span class="contact-text">
              <span class="tel-label">{{ safeT('about.welcome.tel', 'TEL:') }}</span>{{ safeT('about.welcome.phone', '+8619290808990') }}
            </span>
          </div>
        </div>

        <p class="promise-text">{{ safeT('about.welcome.promise', 'We promise to provide an efficient response on every working day.') }}</p>
      </div>
    </section>

    <!-- 页脚 -->
    <WebsiteFooter />
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import WebsiteNavbar from './components/WebsiteNavbar.vue'
import WebsiteFooter from './components/WebsiteFooter.vue'

const { t } = useI18n()

// Safe translation function to handle potential i18n compilation errors
const safeT = (key: string, defaultValue?: string): string => {
  try {
    const result = t(key)
    // If translation returns the key itself, it means translation was not found
    return result === key ? (defaultValue || key) : result
  } catch (error) {
    console.warn(`i18n translation error for key: ${key}`, error)
    return defaultValue || key
  }
}

// Logo 图片
const logoImage = new URL('@/assets/logo.png', import.meta.url).href
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Bitter:wght@300;400;500;600;700&family=Hind:wght@300;400;500;600;700&display=swap');

.about-page {
  font-family: 'Hind', 'Bitter', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  color: #333;
  overflow-x: hidden;
}

/* 灰色横幅区域 */
.hero-section {
  position: relative;
  color: white;
  padding: 120px 0 80px;
  text-align: center;
  margin-top: 75px;
  background-color: #7d7d7d;
}

.hero-background {
  position: absolute;
  inset: 0;
  z-index: 0;
}

.hero-overlay {
  position: absolute;
  inset: 0;
  background-color: rgba(125, 125, 125, 0.9);
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 50px;
  position: relative;
  z-index: 10;
}

.page-title {
  font-size: 58px;
  font-weight: 700;
  margin-bottom: 20px;
  line-height: 1.2;
  text-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  font-family: 'Bitter', serif;
  letter-spacing: -0.5px;
}

.breadcrumb {
  font-size: 14px;
  opacity: 0.95;
  font-weight: 300;
  font-family: 'Hind', sans-serif;
}

.breadcrumb .separator {
  margin: 0 8px;
}

.breadcrumb .current {
  font-weight: 500;
}

/* 公司介绍区域 */
.intro-section {
  padding: 80px 0;
  background-color: #ffffff;
}

.intro-text {
  font-size: 16px;
  line-height: 1.8;
  color: #333;
  max-width: 1000px;
  margin: 0 auto;
  text-align: justify;
  font-family: 'Hind', sans-serif;
}

/* Welcome 和联系方式区域 */
.welcome-section {
  padding: 80px 0;
  background-color: #f5f6f8;
  text-align: center;
}

.logo-container {
  margin-bottom: 30px;
  display: flex;
  justify-content: center;
}

.company-logo {
  height: 120px;
  max-width: 200px;
  object-fit: contain;
}

.welcome-title {
  font-size: 42px;
  font-weight: 700;
  margin-bottom: 50px;
  color: #2c3e50;
  font-family: 'Bitter', serif;
}

.contact-info {
  max-width: 600px;
  margin: 0 auto 40px;
}

.contact-heading {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 30px;
  color: #2c3e50;
  font-family: 'Hind', sans-serif;
}

.contact-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 20px;
  font-size: 16px;
}

.contact-item .icon {
  color: #d97f3e;
  flex-shrink: 0;
}

.contact-link {
  color: #333;
  text-decoration: none;
  transition: color 0.3s;
  font-family: 'Hind', sans-serif;
}

.contact-link:hover {
  color: #d97f3e;
}

.contact-text {
  color: #333;
  font-family: 'Hind', sans-serif;
}

.tel-label {
  margin-right: 4px;
}

.promise-text {
  font-size: 14px;
  color: #666;
  max-width: 600px;
  margin: 0 auto;
  font-style: italic;
  font-family: 'Hind', sans-serif;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .hero-section {
    padding: 100px 0 60px;
    margin-top: 65px;
  }

  .container {
    padding: 0 20px;
  }

  .page-title {
    font-size: 38px;
  }

  .breadcrumb {
    font-size: 12px;
  }

  .intro-section {
    padding: 60px 0;
  }

  .intro-text {
    font-size: 15px;
    text-align: left;
  }

  .welcome-section {
    padding: 60px 0;
  }

  .company-logo {
    height: 80px;
  }

  .welcome-title {
    font-size: 32px;
    margin-bottom: 35px;
  }

  .contact-heading {
    font-size: 18px;
  }

  .contact-item {
    font-size: 14px;
  }

  .promise-text {
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .page-title {
    font-size: 30px;
  }

  .breadcrumb {
    font-size: 10px;
  }

  .breadcrumb .separator {
    margin: 0 4px;
  }
}
</style>
